import React from 'react';
import {Popup} from 'antd-mobile';
import {CloseOutline} from 'antd-mobile-icons';
import Text from '../text';
import {BasicObject} from '@/types';
import {debounce} from '@/utils';
import NoResult from '../error-pages/no-result';
import {useTranslation} from 'react-i18next';
import './picker.css';

export interface PickerProps<T, R> {
  disabled?: boolean;
  title?: string;
  titleRender?: React.JSX.Element;
  /** 打开的抽屉title */
  sheetHeaderTitle?: string;
  /** 自定义打开的抽屉title */
  sheetHeaderTitleRender?: React.JSX.Element;
  list: T[];
  itemRender?: (item: T, index: number) => React.JSX.Element;
  /** 默认取value */
  valueKey?: string;
  /** 默认取label */
  labelKey?: string;
  /** 每一项的高度,默认48,当传入titleRender时,要传入这个值 */
  itemHeight?: number;
  open: boolean;
  setOpen: (open: boolean) => void;
  /** 是否禁止用户关闭,若传入,点击背景不会关闭,以及 */
  disableUserClose?: boolean;
  value?: R;
  onValueChange?: (value: R) => void;
}

const Picker = <T, R>({
  title = 'Please Choose',
  disabled = false,
  titleRender,
  sheetHeaderTitle = 'Please Choose',
  sheetHeaderTitleRender,
  list,
  itemRender,
  labelKey = 'label',
  valueKey = 'value',
  itemHeight = 48,
  open,
  setOpen,
  value,
  onValueChange,
  disableUserClose = false,
}: PickerProps<T, R>) => {
  const {i18n} = useTranslation();
  const [keyWord, setKeyWord] = React.useState('');
  const [finalList, setFinalList] = React.useState<T[]>(list);
  const onSearch = (v: string) => {
    setKeyWord(() => v);
  };
  const filterList = debounce((key: string) => {
    setFinalList(
      list.filter(
        _v =>
          (_v as BasicObject)[labelKey]
            .toLocaleLowerCase()
            .indexOf(key.toLocaleLowerCase()) > -1,
      ),
    );
  });
  React.useEffect(() => {
    filterList(keyWord);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyWord]);
  React.useEffect(() => {
    if (!open) {
      setKeyWord('');
      filterList('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  return (
    <>
      <div
        className="flex flex-col cursor-pointer"
        onClick={() => !disabled && setOpen(true)}>
        {titleRender ? (
          titleRender
        ) : (
          <div className="p-4">
            <Text main>{title}</Text>
          </div>
        )}
      </div>

      <Popup
        visible={open}
        onMaskClick={() => !disableUserClose && setOpen(false)}
        onClose={() => setOpen(false)}
        bodyClassName="picker-popup-body"
        position="bottom">
        <div className="bg-white flex flex-col rounded-t-lg">
          <div className="flex items-center justify-between px-4 pt-4 h-12">
            <div className="flex-1">
              {sheetHeaderTitleRender || (
                <div>
                  <Text main size="medium" blod>
                    {sheetHeaderTitle}
                  </Text>
                </div>
              )}
            </div>
            {!disableUserClose && (
              <div className="p-2 -mr-2" onClick={() => setOpen(false)}>
                <svg viewBox="0 0 24 24" fill="none" className="size-6">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M16.8359 8.7071C17.2265 8.31657 17.2265 7.6834 16.8359 7.29288C16.4454 6.90237 15.8122 6.90237 15.4217 7.2929L12.0644 10.6503L8.70712 7.2929C8.3166 6.90237 7.68343 6.90237 7.2929 7.29288C6.90237 7.6834 6.90237 8.31657 7.29288 8.7071L10.6502 12.0645L7.29288 15.4219C6.90237 15.8125 6.90237 16.4456 7.2929 16.8361C7.68343 17.2267 8.3166 17.2267 8.70712 16.8361L12.0644 13.4787L15.4217 16.8361C15.8122 17.2267 16.4454 17.2267 16.8359 16.8361C17.2265 16.4456 17.2265 15.8125 16.8359 15.4219L13.4786 12.0645L16.8359 8.7071Z"
                    fill="#9FA5AC"
                  />
                </svg>
              </div>
            )}
          </div>
          {list.length > 10 && (
            <div className="flex items-center px-4 mt-3">
              <div className="flex-1 relative">
                <input
                  className="picker-search-input w-full"
                  value={keyWord}
                  placeholder={i18n.t('label.search')}
                  onChange={e => onSearch(e.target.value)}
                />
                {!!keyWord && (
                  <div
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1"
                    onClick={() => setKeyWord('')}>
                    <CloseOutline className="w-3 h-3 text-gray-400" />
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="mt-4" />
          {finalList.length > 0 ? (
            <div
              className="px-4 overflow-y-auto"
              style={{maxHeight: `${itemHeight * 6}px`}}>
              {finalList.map((item, index) => {
                const data = item as BasicObject;
                return (
                  <div
                    key={index}
                    className="flex items-center py-3 cursor-pointer hover:bg-gray-50 transition-colors"
                    style={{height: `${itemHeight}px`}}
                    onClick={() =>
                      onValueChange && onValueChange(data[valueKey])
                    }>
                    <div className="flex-1">
                      {itemRender ? (
                        itemRender(item, index)
                      ) : (
                        <Text main size="medium">
                          {data[labelKey]}
                        </Text>
                      )}
                    </div>
                    {value === data[valueKey] && (
                      <img
                        className="w-3 h-3 ml-4"
                        src={require('@assets/icons/selected.webp')}
                        alt="selected"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="pb-8">
              <NoResult />
            </div>
          )}
        </div>
      </Popup>
    </>
  );
};

export default Picker;
